import * as i0 from '@angular/core';
import { Injectable, Inject, isDevMode, InjectionToken, inject, NgModule } from '@angular/core';
import * as i1 from '@satin/core/injectors';
import { ServiceLocator, GlobalData } from '@satin/core/injectors';
import { isNotNull, isNull, isNotNullNested, isNotEmptyArray, injectApiGateway, FabGridConversionUtility, isEmptyObject, getMaxRecordsPagingModel, getFilterGroupForActiveRecords, getNestedValue, isNullNested, isDate, convertDate, isEmptyArray } from '@satin/core/utility';
import * as i1$1 from '@angular/common/http';
import { HttpContext, HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import * as i4 from '@satin/core/configuration';
import { ConfigurationEnum, ConfigurationInjector } from '@satin/core/configuration';
import { SharedConstant, INTERCEPTOR_AUTH_TYPE_OVERRIDE, REFRESH_TOKEN_SECONDS_FROM_EXPIRY, USER_AUTHENTICATION_TYPE, DEFAULT_COOKIE_DOMAIN, REFRESH_TOKEN_PATH, PIN_LOGIN_METHOD, PIN_ACCESS_KEY, USER_CREDENTIALS_LOGIN_METHOD, USER_ACCESS_KEY, APP_ACCESS_KEY, USER_LOGIN_USER_KEY, PIN_LOGIN_PIN_KEY, APP_AUTHENTICATION_TYPE, PIN_LOGIN_PATH, USER_LOGIN_PATH, OATH_CLIENT_CREDENTIALS_PATH, REDIRECT_QUERY_PARAM } from '@satin/core/constant';
import { UserManager } from 'oidc-client-ts';
import { of, Subject, BehaviorSubject, throwError, Observable, forkJoin } from 'rxjs';
import { Permission, BaseModel, SearchFilterGroupData, AndOrOperator } from '@satin/core/model';
import { map, catchError, finalize, repeat } from 'rxjs/operators';
import { MessagesService, MessageSuppression } from '@satin/core/components/message';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { isNotNull as isNotNull$1 } from '@fabric/common/utilities';
import * as i4$1 from '@satin/core/components/loading-indicator';
import * as i2 from '@angular/router';
import * as i1$2 from 'ngx-cookie';
import jwtDecode from 'jwt-decode';
import * as i6 from '@angular/material/dialog';
import { FabConfirmationDialogComponent } from '@fabric/components/confirmation-dialog';
import { RSocketService } from '@satin/core/ng-rsocket-rxjs';

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class UserTypeService {
    constructor(globalData) {
        this.globalData = globalData;
    }
    setUserTypeToUser() {
        if (isNotNull(this.globalData.user)) {
            this.globalData.user.isInternal = this.isUserGMInternal();
            this.globalData.user.isSupplier = this.isUserSupplier();
            this.globalData.user.isJointVenture = this.isUserJointVenture();
        }
        else {
            console.log("User object is null in Global Data!");
        }
    }
    isUserSupplier() {
        //If No user exists in global data, assume false
        if (isNull(this.globalData.user)) {
            return false;
        }
        return isNotNull(this.globalData.user.ultimateDuns);
    }
    isUserGMInternal() {
        //If No user exists in global data, assume false
        if (isNull(this.globalData.user)) {
            return false;
        }
        return isNull(this.globalData.user.ultimateDuns);
    }
    isUserJointVenture() {
        //TODO: determine how to determine if a user is a JV user.
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserTypeService, deps: [{ token: i1.GlobalData }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserTypeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserTypeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.GlobalData }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * @updated Marilyn Mao marilyn.mao@gm.com
 * @since 11/17/2022
 */
class UserSessionService {
    constructor(http, globalData, userTypeService, configurationInjector) {
        this.http = http;
        this.globalData = globalData;
        this.userTypeService = userTypeService;
        this.configurationInjector = configurationInjector;
        //TODO: Update to use Injected configuration
        this.localApiEndpoint = this.configurationInjector.getValue(ConfigurationEnum.API_GATEWAY_URL) + 'security/users/permissions';
        this.localProfileApiEndpoint = this.configurationInjector.getValue(ConfigurationEnum.GATEWAY_CONTEXT_PATH)
            + this.configurationInjector.getValue(ConfigurationEnum.USER_PROFILE_CONTROLLER_ENDPOINT) + "get/";
        this.siteCodeEnpoint = SharedConstant.SECURITY_CONTEXT + SharedConstant.SITE_CODE_CONTROLLER_ENDPOINT;
        this.userSession = null;
        this.appNameForSiteCodes = null;
        this.userProfileEndpoint = null;
        this.errorNoUserModel = 'ERROR ::: NO USER FOUND IN MODEL';
        this.errorUserSession = 'Could Not get User Session';
    }
    userProfileConsoleLog() {
        console.log(this.errorNoUserModel);
    }
    getUserProfile() {
        if (isNotNull(this.globalData.user)) {
            this.userProfileEndpoint = this.localProfileApiEndpoint + this.globalData.user.id;
            return this.http
                .get(this.userProfileEndpoint)
                .toPromise()
                .then((response) => {
                if (isNull(response)) {
                    this.userProfileConsoleLog();
                }
                return response;
            }).catch((error) => {
                console.error(this.errorUserSession);
                console.error(error);
            });
        }
        else {
            return Promise.reject("No User Found in Session");
        }
    }
    getUserSession(authType, shouldRefreshSession) {
        // if we have an existing user session and we want to keep the same session alive
        if (isNotNull(this.userSession) && !shouldRefreshSession) {
            return of(this.userSession).toPromise();
        }
        // Fetch User session from the common controller
        const emptyModel = {};
        const httpContext = new HttpContext().set(INTERCEPTOR_AUTH_TYPE_OVERRIDE, authType);
        return this.http
            .get(this.localApiEndpoint, { context: httpContext })
            .toPromise()
            .then((response) => {
            if (isNotNull(response[SharedConstant.USER_PARAMETER])) {
                this.setUserSession(response[SharedConstant.USER_PARAMETER]);
            }
            else {
                this.userProfileConsoleLog();
            }
            return this.userSession;
        }).catch((error) => {
            console.error(this.errorUserSession);
            console.error(error);
        });
    }
    processUserSessionResponse(response) {
        this.globalData.user = response;
        this.userTypeService.setUserTypeToUser();
        if (isNull(this.globalData.user)) {
            console.error("No User Profile from Security Service!");
            return;
        }
        if (isNotNullNested(this.globalData, "user.contact.name")) {
            const names = this.globalData.user.contact.name.split(" ");
            let initials = names[0].substring(0, 1).toUpperCase();
            if (names.length > 1) {
                initials += names[names.length - 1].substring(0, 1).toUpperCase();
            }
            this.globalData.user.contact.initials = initials;
        }
    }
    getPermissionByBusinessObject(businessObjectName) {
        const permissionList = this.getUserPermissionList();
        let foundPermission = new Permission(); //All permissions will default to false.
        if (isNotEmptyArray(permissionList)) {
            for (const permission of permissionList) {
                if (permission.businessObject.name === businessObjectName) {
                    foundPermission = permission;
                    foundPermission.businessObject.id = permission.businessObject.id;
                    foundPermission.businessObject.name = permission.businessObject.name;
                    break;
                }
            }
        }
        if (isNull(foundPermission)) {
            console.log("No Permission found for Business Object:", businessObjectName);
        }
        return foundPermission;
    }
    getRoleByName(roleName) {
        const roleList = this.getUserRoleList();
        let foundRole;
        if (isNotEmptyArray(roleList)) {
            for (const role of roleList) {
                if (role.name === roleName) {
                    foundRole = role;
                    break;
                }
            }
        }
        return foundRole;
    }
    getUserPermissionList() {
        if (isNotNull(this.userSession)) {
            return this.userSession.permissionList;
        }
    }
    getUserRoleList() {
        if (isNotNull(this.userSession)) {
            return this.userSession.roleList;
        }
    }
    setUserSession(userSession) {
        this.userSession = userSession;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserSessionService, deps: [{ token: i1$1.HttpClient }, { token: i1.GlobalData }, { token: UserTypeService }, { token: i4.ConfigurationInjector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserSessionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserSessionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: i1.GlobalData }, { type: UserTypeService }, { type: i4.ConfigurationInjector }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 *
 */
/**
 * This class file is responsible for handling the Client Side Oauth
 * related logic. Behide the scene it uses Oidc-client-ts library
 * to maintain the user session and token etc
 */
/**
 * @author Nilesh Joshi nilesh.joshi@gm.com
 * @since 5/17/2023
 */
const OIDC_CONFIG_SETTING = 'config';
class OidcAuthService {
    /**
     *
     * @param oidcConfig UserManagerSettings
     * @param configurationInjector ConfigurationInjector
     * @param router Router
     * @param globalData GlobalData
     * @param httpClient HttpClient
     */
    constructor(oidcConfig, configurationInjector, userSessionService, globalData, httpClient) {
        this.oidcConfig = oidcConfig;
        this.configurationInjector = configurationInjector;
        this.userSessionService = userSessionService;
        this.globalData = globalData;
        this.httpClient = httpClient;
        /**
         * permission endpoint full path
         */
        this.localApiEndpoint = this.configurationInjector.getValue(ConfigurationEnum.API_GATEWAY_URL) + 'security/users/permissions';
        /**
         * variable to detect user login change
         */
        this.loginChangedSubject = new Subject();
        /**
         * login change observable
         */
        this.loginChanged$ = this.loginChangedSubject.asObservable();
        /**
         * Login completed Subject
         */
        this.isloginCompleted = new BehaviorSubject(false);
        /**
         * Login completed Observable
         */
        this.isloginCompleted$ = this.isloginCompleted.asObservable();
        this.userManager = new UserManager({ ...oidcConfig });
        this.userManager.events.addAccessTokenExpired(_ => {
            this.loginChangedSubject.next(false);
        });
    }
    /**
     * This method handles login button click event.
     * oidc-client-ts library will prepare authorize endpoint url
     * and redirect user to auth server login page.
     *
     * @param authenticationType authenticationType
     * @returns
     */
    login() {
        return this.userManager.signinRedirect();
    }
    /**
     * This method handles login using pop up.
     * oidc-client-ts library will prepare authorize endpoint url
     * and redirect user to auth server login page.
     *
     * @param authenticationType authenticationType
     * @returns
     */
    async popupSignIn() {
        try {
            return await this.userManager.signinPopup({});
        }
        catch (e) {
            console.error('Error on opening the pop up');
        }
    }
    /**
     * This method completes the login process as per
     * oAuth specification which includes handling the code and
     *  calling the token endpoint, setting the userManager
     * and calling permission endpoint
     *
     * @returns Promise<User>
     */
    async completeSignInCallbackPopup() {
        try {
            return await this.userManager.signinPopupCallback();
        }
        catch (e) {
            console.error('Error on completing signin call back on pop up window');
        }
    }
    /**
     * This method call oidc signoutRedirect method
     */
    logout() {
        this.userManager.signoutRedirect();
    }
    /**
     *
     * @returns UserManager
     */
    getUserManager() {
        return this.userManager;
    }
    /**
     * This methods checks user is login or not
     *
     * @returns Promise<boolean>
     */
    isLoggedIn() {
        return this.userManager.getUser().then(user => {
            const userCurrent = !!user && !user.expired;
            if (this.user !== user) {
                this.loginChangedSubject.next(userCurrent);
            }
            this.user = user;
            return userCurrent;
        });
    }
    /**
     *
     * @returns Observable<boolean>
     */
    isLoginStatusChanged() {
        return this.loginChanged$;
    }
    /**
     * return login complete Observable
     * @returns Observable<boolean>
     */
    isLoginCompleted$() {
        return this.isloginCompleted$;
    }
    /**
     * This method completes the login process as per
     * oAuth specification which includes handling the code and
     *  calling the token endpoint, setting the userManager
     * and calling permission endpoint
     *
     * @returns Promise<User>
     */
    async completeLogin() {
        try {
            const userResponse = await this.userManager.signinRedirectCallback();
            this.user = userResponse;
            this.loginChangedSubject.next(!!userResponse && !userResponse.expired);
            await this.getUserPermission();
            this.isloginCompleted.next(true);
            return userResponse;
        }
        catch (e) {
            console.error('Something went wrong in the auth-service completing the login.');
        }
    }
    /**
     * This method completes the logout process as per
     * oAuth specification
     *
     * @returns Promise<SignoutResponse>
     */
    completeLogout() {
        this.user = null;
        this.loginChangedSubject.next(false);
        this.isloginCompleted.next(false);
        return this.userManager.signoutRedirectCallback();
    }
    /**
     * This method return access token
     *
     * @returns Promise<string>
     */
    getAccessToken() {
        return this.userManager.getUser().then(user => {
            if (isNotNull(user) && !user.expired) {
                return user.access_token;
            }
            return null;
        });
    }
    /**
     * This method calls permission endpoint to load
     * Global data
     */
    async getUserPermission() {
        try {
            const permissionResponse = await this.httpClient
                .get(this.localApiEndpoint)
                .toPromise();
            if (isNotNull(permissionResponse[SharedConstant.USER_PARAMETER])) {
                this.userSessionService.processUserSessionResponse(permissionResponse[SharedConstant.USER_PARAMETER]);
                this.processUserPermissionResponse(permissionResponse[SharedConstant.USER_PARAMETER]);
            }
            return true;
        }
        catch (e) {
            console.error('Error on calling Permission endpoint');
        }
    }
    /**
     * This method returns oidc auth User object
     * if token is not expired else returns null
     *
     * @returns Promise<User>
     */
    getUser() {
        return this.userManager.getUser().then(user => {
            if (isNotNull(user) && !user.expired) {
                return user;
            }
            return null;
        });
    }
    /**
     * This method check both access token and session
     * are expired or not if expired then it will remove
     *  the expired token from session store and also stops
     *  the silent renew flow
     * @param shouldRouteToLogin boolean
     * @returns Promise<void>
     */
    checkTokenAndSessionExpire() {
        return this.userManager.getUser().then(user => {
            if (isNotNull(user) && user.expired && isNull(user.session_state)) {
                //Remove invalid token
                //NOTE: stopSilentRenew is required else it constanly 
                //Make API call to authroize endpoint. Since session with 
                //STS is not active, API call to authorize endpoint will fail
                this.userManager.removeUser();
                this.userManager.stopSilentRenew();
                this.isloginCompleted.next(false);
                return true;
            }
            return false;
        });
    }
    /**
     * Based on the response from the permission endpoint
     * this method will load GlobalData variable
     *
     * @param response any
     * @returns void
     */
    processUserPermissionResponse(response) {
        this.globalData.user = response;
        this.setUserTypeToUser();
        if (isNull(this.globalData.user)) {
            console.error("No User Profile from Security Service!");
            return;
        }
        if (isNotNullNested(this.globalData, "user.contact.name")) {
            const names = this.globalData.user.contact.name.split(" ");
            let initials = names[0].substring(0, 1).toUpperCase();
            if (names.length > 1) {
                initials += names[names.length - 1].substring(0, 1).toUpperCase();
            }
            this.globalData.user.contact.initials = initials;
        }
    }
    setUserTypeToUser() {
        if (isNotNull(this.globalData.user)) {
            this.globalData.user.isInternal = !isNull(this.globalData.user) && isNotNull(this.globalData.user.ultimateDuns);
            this.globalData.user.isSupplier = !isNull(this.globalData.user) && isNull(this.globalData.user.ultimateDuns);
            this.globalData.user.isJointVenture = false;
        }
        else {
            console.log("User object is null in Global Data!");
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthService, deps: [{ token: OIDC_CONFIG_SETTING }, { token: i4.ConfigurationInjector }, { token: UserSessionService }, { token: i1.GlobalData }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthService, providedIn: "root" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [OIDC_CONFIG_SETTING]
                }] }, { type: i4.ConfigurationInjector }, { type: UserSessionService }, { type: i1.GlobalData }, { type: i1$1.HttpClient }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class AppLoadService {
    constructor(globalData, userSessionService, oidAuthService) {
        this.globalData = globalData;
        this.userSessionService = userSessionService;
        this.oidAuthService = oidAuthService;
    }
    async buildSecurityProfile() {
        const accessToken = await this.oidAuthService.getAccessToken();
        if (isNotNull(accessToken)) {
            try {
                const userPermisisons = await this.oidAuthService.getUserPermission();
                this.oidAuthService.isloginCompleted.next(true);
                return userPermisisons;
            }
            catch (e) {
                console.error('Error on calling ');
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AppLoadService, deps: [{ token: i1.GlobalData }, { token: UserSessionService }, { token: OidcAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AppLoadService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AppLoadService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.GlobalData }, { type: UserSessionService }, { type: OidcAuthService }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/*
* This class is the heart of the CRUD operations.  All services should extend this class.
* All HTTP calls should go through this class so we can better control the interactions
* */
class CommonService {
    setIsLoading(isLoading) {
        this.loading = isLoading;
    }
    isLoading() {
        return this.loading;
    }
    toggleLoading() {
        this.loading = !this.loading;
    }
    /**
     * Assigns the name of the model to the class - this is what the data contract is called from the back end.
     *
     * @param modelName The name of the JSON object coming from the back end
     */
    setModelName(modelName) {
        this.modelName = modelName;
    }
    /**
     * Assigns the api gateway the service will be hitting
     *
     * @param apiGateway This is the api gateway this service will be hitting
     */
    setApiGateway(apiGateway) {
        this.apiGateway = apiGateway;
    }
    /**
     * Returns the configured Api Gateway
     */
    getApiGateway() {
        return this.apiGateway;
    }
    /**
     * Assigns the endpoint the service will be hitting
     *
     * @param apiEndpoint This is the endpoint this service will be hitting
     */
    setApiEndpoint(apiEndpoint) {
        let endpoint;
        if (isNotNull(this.apiGateway)) {
            this.apiEndpoint = injectApiGateway(apiEndpoint, this.apiGateway);
        }
        else {
            this.apiEndpoint = apiEndpoint;
        }
    }
    /**
     * Returns the configured Api Endpoint
     */
    getApiEndpoint() {
        return this.apiEndpoint;
    }
    /**
     * Assigns the primary key of a data element
     *
     * @param primaryKeyName This defines the primary key for the object to determine if as save will update or create
     */
    setPrimaryKey(primaryKeyName) {
        this.primaryKeyName = primaryKeyName;
    }
    /**
     * Assigns a value to createAttributeIndicator.
     * ABSENCE of this value will determine that the record needs to be created versus updated
     *
     * @param createAttributeIndicator The name of the value used to determine if this data is create or update
     */
    setCreateAttributeIndicator(createAttributeIndicator) {
        this.createAttributeIndicator = createAttributeIndicator;
    }
    constructor() {
        this.headers = null;
        this.http = ServiceLocator.injector.get(HttpClient);
        this.messageService = ServiceLocator.injector.get(MessagesService);
        this.globalData = ServiceLocator.injector.get(GlobalData);
        this.configurationInjector = ServiceLocator.injector.get(ConfigurationInjector);
        this.modelName = null;
        this.apiEndpoint = null;
        this.apiGateway = null;
        this.primaryKeyName = null;
        this.createAttributeIndicator = null;
        this.loading = false;
        this.createEndpointSuffix = "";
        this.updateEndpointSuffix = "";
        this.deleteEndpointSuffix = "";
        this.getEndpointSuffix = "";
        this.searchEndpointSuffix = "";
        this.listEndpointSuffix = "";
        this.resultListVariableName = "";
        this.useAdvancedHttpMethods = false;
        //Define commonly used headers
        this.headers = new HttpHeaders();
        this.headers.set('Content-Type', 'application/json');
        this.headers.set('Accept', 'application/json');
        this.manageConfigurations();
    }
    manageConfigurations() {
        if (isNull(this.primaryKeyName)) {
            this.primaryKeyName = SharedConstant.PRIMARY_KEY_PARAMETER_DEFAULT;
        }
        if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.RESULT_LIST_VARIABLE_NAME))) {
            this.resultListVariableName = this.configurationInjector.getValue(ConfigurationEnum.RESULT_LIST_VARIABLE_NAME);
        }
        else {
            this.resultListVariableName = SharedConstant.RESULT_LIST_PARAMETER;
        }
        /*
        Advanced HTTP Methods will utilize the following:
        Creates are POST
        ID get is GET
        List is GET
        Update is PUT
        Delete is DELETE
        Search is POST
         */
        if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_ADVANCED_METHODS))) {
            this.useAdvancedHttpMethods = this.configurationInjector.getValue(ConfigurationEnum.HTTP_ADVANCED_METHODS);
        }
        if (this.useAdvancedHttpMethods) {
            // Since we only use Search for suffixes, check to see if it's overriden:
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_SEARCH_API_SUFFIX))) {
                this.searchEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_SEARCH_API_SUFFIX);
            }
            else {
                this.searchEndpointSuffix = SharedConstant.HTTP_SEARCH_EXTENSION;
            }
        }
        else {
            // Check to see if anything is override in the configuration
            // SEARCH Endpoint
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_SEARCH_API_SUFFIX))) {
                this.searchEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_SEARCH_API_SUFFIX);
            }
            else {
                this.searchEndpointSuffix = SharedConstant.HTTP_SEARCH_EXTENSION;
            }
            // Create Endpoint
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_CREATE_API_SUFFIX))) {
                this.createEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_CREATE_API_SUFFIX);
            }
            else {
                this.createEndpointSuffix = SharedConstant.HTTP_CREATE_EXTENSION;
            }
            // Update Endpoint
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_UPDATE_API_SUFFIX))) {
                this.updateEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_UPDATE_API_SUFFIX);
            }
            else {
                this.updateEndpointSuffix = SharedConstant.HTTP_UPDATE_EXTENSION;
            }
            // Delete Endpoint
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_DELETE_API_SUFFIX))) {
                this.deleteEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_DELETE_API_SUFFIX);
            }
            else {
                this.deleteEndpointSuffix = SharedConstant.HTTP_DELETE_EXTENSION;
            }
            // Get Endpoint
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_GET_API_SUFFIX))) {
                this.getEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_GET_API_SUFFIX);
            }
            else {
                this.getEndpointSuffix = SharedConstant.HTTP_GET_EXTENSION;
            }
            // List Endpoint
            if (isNotNull(this.configurationInjector.getValue(ConfigurationEnum.HTTP_LIST_API_SUFFIX))) {
                this.listEndpointSuffix = this.configurationInjector.getValue(ConfigurationEnum.HTTP_LIST_API_SUFFIX);
            }
            else {
                this.listEndpointSuffix = SharedConstant.HTTP_LIST_EXTENSION;
            }
        }
    }
    /**
     * Sometimes we just need to do a simple GET.
     * This is useful for getting lists or non-standard JSON formats
     *
     * @param endpoint: The endpoint of the GET call
     * @returns Observable<T> the JSON object from the backend response
     */
    simpleGet({ endpoint, httpOptions }) {
        //Construct model for Filtering
        return this.http
            .get(endpoint, httpOptions).pipe(map(response => {
            // TODO: do validation mapping.
            return response;
        }), catchError(error => this.handleError(error)));
    }
    /**
     * Sometimes we just need to do a simple POST.
     * This is useful for getting lists or non-standard JSON formats
     *
     * @param data POST data to send
     * @param endpoint The endpoint of the POST call
     * @param options Any extra POST options
     * @returns Observable<T> the JSON object from the backend response
     */
    simplePost({ data, endpoint, options = {} }) {
        return this.http
            .post(endpoint, data, options).pipe(map(response => {
            if (!this.messageService.processMessage(response)) {
                throw throwError(response);
            }
            // TODO: do validation mapping.
            return response;
        }), catchError(error => this.handleError(error)));
    }
    /**
     * Sometimes we just need to do a simple PUT.
     *
     * @param data PUT data to send
     * @param endpoint The endpoint of the PUT call
     * @param options Any extra PUT options
     * @returns Observable<T> the JSON object from the backend response
     */
    simplePut({ data, endpoint, options = {} }) {
        return this.http
            .put(endpoint, data, options).pipe(map(response => {
            if (!this.messageService.processMessage(response)) {
                throw throwError(response);
            }
            return response;
        }), catchError(error => this.handleError(error)));
    }
    /**
     * simple Patch.
     *
     * @param data Patch data to send
     * @param endpoint The endpoint of the Patch call
     * @param options Any extra PATCH ptions
     * @returns Observable<T> the JSON object from the backend response
     */
    simplePatch({ data, endpoint, options = {} }) {
        return this.http
            .patch(endpoint, data, options).pipe(map(response => {
            if (!this.messageService.processMessage(response)) {
                throw throwError(response);
            }
            return response;
        }), catchError(error => this.handleError(error)));
    }
    /**
     * Sometimes we just need to do a simple DELETE.
     *
     * @param endpoint The endpoint of the DELETE call
     * @param options Any extra DELETE options
     * @returns Observable<T> the JSON object from the backend response
     */
    simpleDelete({ endpoint, options = {} }) {
        return this.http
            .delete(endpoint, options).pipe(map(response => {
            if (!this.messageService.processMessage(response)) {
                throw throwError(response);
            }
            return response;
        }), catchError(error => this.handleError(error)));
    }
    /**
     * When we need a list for a dropdown.  We're expecting an array of data objects from the back end
     *
     * @param id: OPTIONAL: If we need to append an ID to the end of the endpoint
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param cacheOverride: OPTIONAL: if we're caching the list in the browser, this is the name of that list
     * @param messageSuppression: OPTIONAL: any message suppression settings
     *
     * @returns Observable<T[]> Returns an observable with a list of T
     */
    list({ id = '', endpointOverride = '', modelOverride = '', cacheOverride = '', messageSuppression = new MessageSuppression(false, false, false) }) {
        this.checkDependencies();
        let url = this.getEndpoint(this.listEndpointSuffix, endpointOverride);
        if (isNotNull(id)) {
            url += "/" + id;
        }
        // See if it exists in the Cache, otherwise fetch it.
        if (isNotNull(cacheOverride) && isNotEmptyArray(this.globalData.cacheMap.get(cacheOverride))) {
            // create observable
            return new Observable((observer) => {
                // observable execution
                observer.next(this.globalData.cacheMap.get(cacheOverride));
                observer.complete();
            });
        }
        return this.http
            .get(url).pipe(map(response => {
            if (isNotNull(response)) {
                const list = this.processListFromResponse(response, modelOverride, messageSuppression);
                //Lets determined if we should add it to cache:
                if (isNotNull(cacheOverride)) {
                    this.globalData.cacheMap.set(cacheOverride, list);
                }
                //Just return the list, we don't care about paging
                return list;
            }
        }), catchError(error => this.handleError(error)));
    }
    /**
     * Most functions will process a similarly formatted response from the back end.  This will process that.
     *
     * @param response: the response object from the back end
     * @param modelOverride: OPTIONAL: Whatever we need to map the model too if it doesn't match the base
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns any: should return the object we're expecting from the back end without the metadata.
     */
    processListFromResponse(response, modelOverride, messageSuppression) {
        // Processes all the messages from the response, this function will return true if there are no error messages
        // Expected variables are already hard coded in the message service
        if (this.messageService.processMessage(response, messageSuppression)) {
            if (isNotNull(response[this.resultListVariableName])) {
                return response[this.resultListVariableName];
                //Has a Data Element in the Model?  This is mainly used for angular mock web api
            }
            else if (isNotNull(modelOverride) && isNotNull(response[modelOverride])) {
                return response[modelOverride];
                //Has a Data Element in the Model?  This is mainly used for angular mock web api
            }
            else {
                return response;
            }
        }
    }
    /**
     * This is a search function - usually we marry these to Grids
     *
     * @param model: OPTIONAL: This is the search model coming from the grid
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     * @param filterGroup: OPTIONAL: any additional filters that needs to be added
     * @param paging: OPTIONAL: paging data for the search
     * @param endpointOverride: OPTIONAL: override for the api endpoint
     *
     * @returns any: should return the object we're expecting from the back end without the metadata.
     */
    search({ fabGridState, messageSuppression = new MessageSuppression(false, false, false), filterGroup, paging, endpointOverride = '', httpOptions }) {
        this.checkDependencies();
        const url = this.getEndpoint(this.searchEndpointSuffix, endpointOverride);
        //Construct model for Filtering
        let postModel = {};
        if (isNotNull(fabGridState)) {
            postModel = FabGridConversionUtility.convertFabGridToSearchModel(fabGridState);
        }
        if (isNotNull(filterGroup)) {
            postModel = this.addFilterGroupToPostModel(filterGroup, postModel);
        }
        if (isNotNull(paging)) {
            // if the call is only passing in a paging model group we build a new base model.
            if (isEmptyObject(postModel)) {
                postModel = new BaseModel();
            }
            postModel.paging = paging;
        }
        //Actual HTTP code will use posts - So this will need to be validated.
        return this.http
            .post(url, postModel, httpOptions).pipe(map(response => {
            let outModel = new BaseModel();
            // Processes all the messages from the response, this function will return true if there are no error messages
            // Expected variables are already hard coded in the message service
            if (this.messageService.processMessage(response, messageSuppression)) {
                if (isNotNull(response[this.resultListVariableName])) {
                    outModel.resultList = response[this.resultListVariableName];
                    //Has a Data Element in the Model?  This is mainly used for angular mock web api
                }
                else if (isNotNull(response[SharedConstant.DATA_LIST_PARAMETER])) {
                    outModel.resultList = response[SharedConstant.DATA_LIST_PARAMETER];
                    //If neither just map model for model
                }
                else {
                    outModel = response;
                }
                outModel.paging = response[SharedConstant.PAGING_PARAMETER];
            }
            this.toggleLoading();
            return outModel;
        }), catchError(error => this.handleError(error)));
    }
    /**
     * This method is used to search for maximum active records with state and message suppress
     * @param gridState : grid state we need to search on
     */
    searchForMaximumActiveRecordsWithState(gridState) {
        return this.search({
            fabGridState: gridState,
            paging: getMaxRecordsPagingModel(),
            filterGroup: getFilterGroupForActiveRecords({}),
            messageSuppression: new MessageSuppression(true, true, false)
        });
    }
    getById(id, httpOptions) {
        return this.get({ id: id, httpOptions: httpOptions });
    }
    /**
     * This function will get an object based on an ID
     *
     * @param id: This is the primary key of the record being accessed
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns any: should return the object we're expecting from the back end without the metadata.
     */
    get({ id, endpointOverride = '', modelOverride = '', messageSuppression = new MessageSuppression(false, false, false), httpOptions }) {
        if (isNull(id)) {
            console.error("commonService.getById - No ID passed into the function");
            return;
        }
        this.checkDependencies();
        //Set Default Model Name
        let modelName = this.modelName;
        //Check if an override is passed in
        if (isNotNull(modelOverride)) {
            modelName = modelOverride;
        }
        //Set out default Endpoint
        let url = this.getEndpoint(`${this.getEndpointSuffix}/${id}`, endpointOverride);
        //Sometimes we need to pass in an endpoint override.  This will still add the ID at the end, but with no /get
        if (isNotNull(endpointOverride)) {
            //lets check to make sure hte last character of the URL has a '/'
            if (url.substr(url.length - 1) !== "/") {
                url += "/";
            }
            //Finally add the ID at the end
            url += id;
        }
        return this.http
            .get(url, httpOptions).pipe(map(response => {
            // Processes all the messages from the response, this function will return true if there are no error messages
            // Expected variables are already hard coded in the message service
            if (!this.messageService.processMessage(response, messageSuppression)) {
                throw throwError(response);
            }
            return this.getModelFromResponse(response, modelName);
        }), catchError(error => this.handleError(error)));
    }
    /**
     * This function will get an object based on an ID
     *
     * @param id: This is the primary key of the record being accessed
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     *
     * @returns any: should return the object we're expecting from the back end without the metadata.
     */
    download({ id, endpointOverride = '', modelOverride = '' }) {
        if (isNull(id)) {
            console.error("commonService.getById - No ID passed into the function");
            return;
        }
        this.checkDependencies();
        //Set Default Model Name
        // let modelName = this.modelName; // Sonar scan says this is unnecessary, changed 282
        //Check if an override is passed in
        if (isNotNull(modelOverride)) {
            this.modelName = modelOverride;
        }
        //Set out default Endpoint
        let url = this.getEndpoint(`${this.getEndpointSuffix}/${id}`, endpointOverride);
        //Sometimes we need to pass in an endpoint override.  This will still add the ID at the end, but with no /get
        if (isNotNull(endpointOverride)) {
            //lets check to make sure hte last character of the URL has a '/'
            if (url.substr(url.length - 1) !== "/") {
                url += "/";
            }
            //Finally add the ID at the end
            url += id;
        }
        return this.http
            .get(url, { responseType: 'blob' }).pipe(catchError(error => this.handleError(error)));
    }
    /**
     * This is where we persist an object to the back end.
     * Based on the presence of a Primary Key it will create or update accordingly
     *
     * @param data: The Object you want to persist to the back end
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    save({ data, endpointOverride = '', modelOverride = '', responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false), httpOptions }) {
        //Check for the presence of a primary key value.  If it's null, we'll run the create, otherwise update
        if (this.useCreateEndpoint(data)) {
            return this.create({ data, endpointOverride, modelOverride, responseFilterOverride, messageSuppression, httpOptions });
        }
        else {
            return this.update({ data, endpointOverride, modelOverride, responseFilterOverride, messageSuppression, httpOptions });
        }
    }
    /**
     * NOTE: This method has not been tested yet.
     * This is where we persist a list of objects to the back end.
     * Based on the presence of a Primary Key it will create or update accordingly
     *
     * @param data: The Object you want to persist to the back end
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    saveList({ data, endpointOverride = '', modelOverride = '', responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false) }) {
        const source = data.map(value => this.save({
            data: value,
            endpointOverride: endpointOverride,
            modelOverride: modelOverride,
            responseFilterOverride: responseFilterOverride,
            messageSuppression: messageSuppression
        }));
        return forkJoin(source);
    }
    /**
     * This is where we persist an object to the back end to do an update endpoint
     *
     * @param data: The Object you want to persist to the back end
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    update({ data, endpointOverride = '', modelOverride = '', responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false), httpOptions }) {
        this.checkDependencies();
        const url = this.getEndpoint(this.updateEndpointSuffix, endpointOverride);
        if (this.useAdvancedHttpMethods) {
            return this.persistAsPut({ data, endpoint: url, modelOverride, responseFilterOverride, messageSuppression, httpOptions });
        }
        else {
            return this.persistAsPost({ data, endpoint: url, modelOverride, responseFilterOverride, messageSuppression, httpOptions });
        }
    }
    /**
     * This method shoud be used when we want to update one or more fields
     * unlike the put, patch does not require to send all fields
     *
     * @param data: The Object with fields we want to update
     * @param patchEndpoint: Required
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    patch({ data, patchEndpoint, modelOverride = '', responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false) }) {
        this.checkDependencies();
        const patchModel = this.getModel(data, modelOverride);
        return this.http
            .patch(patchEndpoint, patchModel).pipe(map(response => {
            return this.returnResultsFromPersistence({
                response: response,
                modelOverride: modelOverride,
                responseFilterOverride: responseFilterOverride,
                messageSuppression: messageSuppression
            });
        }), catchError(error => this.handleError(error)));
    }
    /**
     * This is where we persist an object to the back end to do a create endpoint
     *
     * @param data: The Object you want to persist to the back end
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    create({ data, endpointOverride = '', modelOverride = '', responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false), httpOptions }) {
        this.checkDependencies();
        let url = this.getEndpoint(this.createEndpointSuffix, endpointOverride);
        return this.persistAsPost({ data, endpoint: url, modelOverride, responseFilterOverride, messageSuppression, httpOptions });
    }
    /**
     * This is where we persist an object to the back end to do an update endpoint
     *
     * @param data: The Object you want to persist to the back end
     * @param endpoint: endpoint to use
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    persistAsPut({ data, endpoint, modelOverride, responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false), httpOptions, }) {
        const putModel = this.getModel(data, modelOverride);
        endpoint = this.addPrimaryKeyToEndpoint(endpoint, data);
        return this.http
            .put(endpoint, putModel, httpOptions).pipe(map(response => {
            return this.returnResultsFromPersistence({
                response: response,
                modelOverride: modelOverride,
                responseFilterOverride: responseFilterOverride,
                messageSuppression: messageSuppression
            });
        }), catchError(error => this.handleError(error)));
    }
    /**
     * This is where we persist an object to the back end to do an update endpoint
     *
     * @param data: The Object you want to persist to the back end
     * @param endpoint: endpoint to use
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    persistAsPost({ data, endpoint, modelOverride, responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false), httpOptions }) {
        const postModel = this.getModel(data, modelOverride);
        return this.http
            .post(endpoint, postModel, httpOptions).pipe(map(response => {
            return this.returnResultsFromPersistence({
                response: response,
                modelOverride: modelOverride,
                responseFilterOverride: responseFilterOverride,
                messageSuppression: messageSuppression
            });
        }), catchError(error => this.handleError(error)));
    }
    /**
     * This is where we return the results from creates or updates
     *
     * @param data: The Object you want to persist to the back end
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    returnResultsFromPersistence({ response, modelOverride, responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false) }) {
        // Processes all the messages from the response, this function will return true if there are no error messages
        // Expected variables are already hard coded in the message service
        if (!this.messageService.processMessage(response, messageSuppression)) {
            // The Exception is stopping the forkJoin from finalizing if multiple runs are kicked off
            // in saveList.  We don't actually use the catch though, so we can move on with this and
            // Just return a null I think.
            // throw observableThrowError(response);
            return null;
        }
        // Sometimes we want to get the entire response back
        if (responseFilterOverride) {
            return response;
        }
        return this.getModelFromResponse(response, modelOverride);
    }
    /**
     * This is where we delete an object from the back end (or expire, or whatever).  It runs the delete endpoint.
     *
     * @param data: The Object you want to persist to the back end
     * @param endpointOverride: OPTIONAL: if the endpoint is different than the base endpoint for the service
     * @param modelOverride: OPTIONAL: If there is a model coming back from the backend that doesn't match the service
     * @param responseFilterOverride: OPTIONAL: If we're overriding the response filter
     * @param messageSuppression: OPTIONAL: any message suppression we're passing in
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    destroy({ data, endpointOverride = '', modelOverride = '', responseFilterOverride = false, messageSuppression = new MessageSuppression(false, false, false) }) {
        this.checkDependencies();
        let url = this.getEndpoint(this.deleteEndpointSuffix, endpointOverride);
        if (this.useAdvancedHttpMethods) {
            url = this.addPrimaryKeyToEndpoint(url, data);
            return this.http
                .delete(url).pipe(map(response => {
                return this.returnResultsFromPersistence({
                    response: response,
                    modelOverride: modelOverride,
                    responseFilterOverride: responseFilterOverride,
                    messageSuppression: messageSuppression
                });
            }), catchError(error => this.handleError(error)));
        }
        else {
            const postModel = this.getModel(data, modelOverride);
            return this.http
                .post(url, postModel).pipe(map(response => {
                return this.returnResultsFromPersistence({
                    response: response,
                    modelOverride: modelOverride,
                    responseFilterOverride: responseFilterOverride,
                    messageSuppression: messageSuppression
                });
            }), catchError(error => this.handleError(error)));
        }
    }
    /**
     * Deletes an item based on ID
     *
     * @param id: The Object you want to persist to the back end based on id
     *
     * @returns Observable<T>: Returns an observable based on the response from the back end.
     */
    destroyById(id) {
        if (isNull(id)) {
            console.error("commonService.destroy - No ID passed into the function");
            return;
        }
        this.checkDependencies();
        let url = this.getEndpoint(this.deleteEndpointSuffix);
        //Construct model for Filtering
        const postModel = {};
        postModel[this.modelName] = {};
        postModel[this.modelName][this.primaryKeyName] = id;
        if (this.useAdvancedHttpMethods) {
            url = this.addPrimaryKeyToEndpoint(url, postModel[this.modelName]);
            return this.http
                .delete(url)
                .toPromise()
                .then(response => {
                this.messageService.processMessage(response);
            })
                .catch(error => this.handleError(error));
        }
        else {
            return this.http
                .post(url, postModel)
                .toPromise()
                .then(response => {
                this.messageService.processMessage(response);
            })
                .catch(error => this.handleError(error));
        }
    }
    /**
     * This is a dev helper function to make sure the service is configured properly
     */
    checkDependencies() {
        // Lets get this garbage out of prod mode
        // Display checks and balances for services.
        if (isDevMode()) {
            if (isNull(this.apiEndpoint)) {
                console.error("No Endpoint is Defined during Construction");
            }
            if (isNull(this.modelName)) {
                console.error("No Model Label is Defined during Construction");
            }
            if (isNull(this.primaryKeyName)) {
                console.log("No Primary Key is Defined during Construction, using default");
            }
        }
    }
    /**
     * Processes the response from the back end.
     *
     * @param response: this is the response JSON object from the back end
     * @param overrideModelName: If we're overriding the model from the backend
     *
     * @returns any: should be the data from the back end
     */
    getModelFromResponse(response, overrideModelName) {
        let outModel;
        if (isNotNull(overrideModelName)) {
            outModel = response[overrideModelName];
        }
        else if (isNotNull(this.modelName)) {
            outModel = response[this.modelName];
        }
        else {
            outModel = response;
        }
        // Just add some friendly logging in case we get nothing from the back end.
        if (isNull(outModel) && isDevMode()) {
            console.error("No Object found for " + this.modelName + " in the response JSON");
        }
        return outModel;
    }
    /**
     * Common Error Handling for all the HTTP subscriptions
     *
     * @param error: This is the error from the subscription
     *
     * @returns Promise<any>: Returns a promise with the error
     */
    handleError(error) {
        console.log("Error in Common Service", error);
        //Show the error message
        if (isNotNull(error.message)) {
            this.messageService.processHttpFailureMessage(error);
        }
        else if (!(error instanceof Object)) {
            this.messageService.processHttpFailureMessage(error);
        }
        //Throw a reject
        return Promise.reject(error.message || error);
    }
    /**
     * Common Code for the Endpoint fetches
     *
     * @param basedEndpoint: base endpoint passed in
     * @param endpointOverride: OPTIONAL: override endpoint passed in
     *
     * @returns Promise<any>: Returns a promise with the error
     */
    getEndpoint(basedEndpoint, endpointOverride) {
        if (isNotNull(endpointOverride)) {
            return endpointOverride;
        }
        else {
            return this.apiEndpoint + basedEndpoint;
        }
    }
    /**
     * Common Code for adding the primary key to the end of the endpoint
     *
     * @param endpoint: base endpoint passed in
     * @param data: data block to get ID from
     *
     * @returns Promise<any>: Returns a promise with the error
     */
    addPrimaryKeyToEndpoint(endpoint, data) {
        const primaryKey = getNestedValue(data, this.primaryKeyName);
        // Adding ID to the end of the Endpoint as that's what REST is expecting
        //lets check to make sure hte last character of the URL has a '/'
        if (endpoint.substr(endpoint.length - 1) !== "/") {
            endpoint += "/";
        }
        //Finally add the ID at the end
        endpoint += primaryKey;
        return endpoint;
    }
    /*
    * Common Code for the Endpoint fetches
     */
    getModel(data, modelOverride) {
        let postModel = {};
        if (isNotNull(modelOverride)) {
            postModel[modelOverride] = data;
        }
        else if (isNotNull(this.modelName)) {
            postModel[this.modelName] = data;
        }
        else {
            postModel = data;
        }
        return postModel;
    }
    /**
     * Adds a search filter to the base mode. If existing search filters are found,
     * it will add the provided filterGroup. If AndOrOperator is not provided, it will
     * AND the filterGroup to the existing criteria.
     *
     * @param filterGroup
     * @param postModel
     * @private
     */
    addFilterGroupToPostModel(filterGroup, postModel) {
        if (isNull(filterGroup)) {
            console.error("Filter group cannot be null!");
            return;
        }
        if (isEmptyObject(postModel)) {
            postModel = new BaseModel();
        }
        if (isNull(postModel.searchFilters)) {
            postModel.searchFilters = new SearchFilterGroupData();
        }
        if (this.hasExistingSearchFilters(postModel)) {
            if (isNull(postModel.searchFilters.andOrOperator)) {
                postModel.searchFilters.andOrOperator = AndOrOperator.and;
            }
            postModel.searchFilters.filterGroups.push(filterGroup);
        }
        else {
            postModel.searchFilters = filterGroup;
        }
        return postModel;
    }
    /**
     * Determines if the base model has existing search filters.
     *
     * @param postModel
     * @private
     */
    hasExistingSearchFilters(postModel) {
        if (isNull(postModel)) {
            console.error("The base model provided is null.");
            return;
        }
        if (isNull(postModel.searchFilters)) {
            return false;
        }
        if (isEmptyObject(postModel.searchFilters)) {
            return false;
        }
        if (isNotEmptyArray(postModel.searchFilters.filterGroups) ||
            isNotEmptyArray(postModel.searchFilters.fieldFilters)) {
            return true;
        }
    }
    useCreateEndpoint(data) {
        // Assume an update otherwise
        let useCreateEndpoint = false;
        // We'll check if a Create Attribute Indicator is set, as sometimes we can't rely on the Primary Key
        if (isNotNull(this.createAttributeIndicator)) {
            if (isNullNested(data, this.createAttributeIndicator)) {
                useCreateEndpoint = true;
                // Otherwise check the existence of a primary key.
            }
            // Otherwise check the existence of a primary key.
        }
        else if (isNotNull(this.primaryKeyName)) {
            if (isNullNested(data, this.primaryKeyName)) {
                useCreateEndpoint = true;
            }
        }
        return useCreateEndpoint;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class CommonFormService {
    constructor() {
        this.formBuilder = ServiceLocator.injector.get(UntypedFormBuilder);
    }
    // This will construct an object based on the incoming Contact Type Object
    // This syntax and structure is only used for Forms
    // Essentially, this will need to contain a list of all inputs on the form and what restrictions are put on place for them.
    buildForm(baseObject, validationMap, hasNestedObjects) {
        // Build a form out of the existing data from the back end.  A default form has no validation
        //Convert Arrays for the form
        let formObject = { ...baseObject };
        formObject = this.convertArraysInObject(formObject);
        //Deal with Nested Objects
        if (isNotNull(hasNestedObjects) && hasNestedObjects) {
            for (const propertyName in formObject) {
                //Here we look to see if there are any nest objects in the base object - But NOT an array
                if (formObject[propertyName] instanceof Object
                    && !(formObject[propertyName] instanceof Array)
                    && !this.isObjectFormControl(formObject[propertyName])) {
                    if (isNotNull(validationMap) && isNotNull(validationMap[propertyName])) {
                        // WARNING: Recursion - Beware of nested nested objects.
                        formObject[propertyName] = this.buildForm(formObject[propertyName], validationMap[propertyName]);
                    }
                    else {
                        // WARNING: Recursion - Beware of nested nested objects.
                        formObject[propertyName] = this.buildForm(formObject[propertyName]);
                    }
                }
            }
        }
        //Dates need to be converted for date picker.
        this.convertDatesForDatePicker(formObject);
        //Build the form before checking for nested objects so we don't run into issues with undefined
        return this.constructForm(formObject, validationMap);
    }
    ;
    convertDatesForDatePicker(formObject) {
        //Dates need to be converted for date picker.
        for (const propertyName in formObject) {
            if (isDate(formObject[propertyName])) {
                formObject[propertyName] = convertDate(formObject[propertyName]);
            }
        }
    }
    constructForm(formObject, validationMap) {
        const form = this.formBuilder.group(formObject);
        // Defined specific validations here
        // Syntax:
        // form.setControl(<name of control itself referenced in html as <formName>.control.<controlName>
        // , new formControl (defaultValue, Validators))
        if (isNotNull(validationMap)) {
            for (const propertyName in validationMap) {
                if (validationMap.hasOwnProperty(propertyName) && formObject.hasOwnProperty(propertyName)) {
                    form.setControl(propertyName, new UntypedFormControl(formObject[propertyName], validationMap[propertyName]));
                }
                else if (validationMap.hasOwnProperty(propertyName)) {
                    form.setControl(propertyName, new UntypedFormControl(null, validationMap[propertyName]));
                }
                else {
                    console.log("No Validation could be done for Property: " + propertyName);
                }
            }
        }
        return form;
    }
    // We might not need this but might as well do some routing for consistency in case we need to remap something in the future.
    buildObject(form) {
        return form.value;
    }
    ;
    convertArraysInObject(object) {
        for (const propertyName in object) {
            if (object[propertyName] instanceof Array) {
                object[propertyName] = [object[propertyName]];
            }
        }
        return object;
    }
    isObjectFormControl(formObject) {
        let isFormControl = false;
        // Only thing I can think of to ID a formObject is the presents of an ID and a name attribute.
        // If we're not using BaseData then we'll have to be a bit more creative
        if (isNotNull(formObject.id) && isNotNull(formObject.name)) {
            isFormControl = true;
        }
        return isFormControl;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonFormService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonFormService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonFormService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class GuidService {
    generateGuid() {
        function s4() {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        }
        return s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: GuidService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: GuidService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: GuidService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class LogoutService {
    constructor() { }
    sendUserToLogout() {
        window.location.href = SharedConstant.LOGOUT_URL;
    }
    sendUserToNoAccess() {
        window.location.href = SharedConstant.NO_ACCESS_URL;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: LogoutService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: LogoutService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: LogoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class PermissionService {
    constructor(globalData) {
        this.globalData = globalData;
    }
    hasReadPermission(businessObjectName) {
        const permission = this.getPermissionByBusinessObject(businessObjectName);
        return permission.canRead;
    }
    hasAddPermission(businessObjectName) {
        const permission = this.getPermissionByBusinessObject(businessObjectName);
        return permission.canCreate;
    }
    hasEditPermission(businessObjectName) {
        const permission = this.getPermissionByBusinessObject(businessObjectName);
        return permission.canUpdate;
    }
    hasDeletePermission(businessObjectName) {
        const permission = this.getPermissionByBusinessObject(businessObjectName);
        return permission.canDelete;
    }
    hasRunPermission(businessObjectName) {
        const permission = this.getPermissionByBusinessObject(businessObjectName);
        return permission.canRun;
    }
    hasReadPermissionForAny(businessObjectArray) {
        let canRead = false;
        // Loop through the array of business objects.  If a user has read on any of them, return true;
        for (const x in businessObjectArray) {
            if (this.hasReadPermission(businessObjectArray[x])) {
                canRead = true;
                break;
            }
        }
        return canRead;
    }
    getPermissionByBusinessObject(businessObjectName) {
        const permissionList = this.getUserPermissionList();
        if (isNotEmptyArray(permissionList)) {
            const foundPermission = permissionList.find(permission => permission.businessObject.name === businessObjectName);
            if (isNotNull$1(foundPermission)) {
                return foundPermission;
            }
        }
        console.error("No Permission found for Business Object:", businessObjectName);
        return new Permission();
    }
    getUserPermissionList() {
        if (isNotNull$1(this.globalData)) {
            return this.globalData.user.permissionList;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: PermissionService, deps: [{ token: i1.GlobalData }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: PermissionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: PermissionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.GlobalData }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class SiteCodeService {
    constructor(http) {
        this.http = http;
        this.siteCodeEnpoint = SharedConstant.SECURITY_CONTEXT + SharedConstant.SITE_CODE_CONTROLLER_ENDPOINT;
    }
    getSiteCodes(userGmin, appAcronym) {
        // Fetch User session from the common controller
        const userModel = {};
        userModel[SharedConstant.BUSINESS_USER_PARAMETER] = {};
        userModel[SharedConstant.BUSINESS_USER_PARAMETER].id = userGmin;
        userModel[SharedConstant.APP_PARAMETER] = {};
        userModel[SharedConstant.APP_PARAMETER].name = appAcronym;
        return this.http
            .post(this.siteCodeEnpoint, userModel).pipe(map((response) => {
            let siteCodes = null;
            if (isNotNull(response[SharedConstant.BUSINESS_USER_PARAMETER])) {
                siteCodes = response[SharedConstant.BUSINESS_USER_PARAMETER][SharedConstant.SITE_CODE_PARAMETER];
            }
            else {
                console.log("ERROR ::: NO USER FOUND IN MODEL");
            }
            // return true to indicate successful login
            return siteCodes;
        }, (error) => {
            console.error("Could Not get Site Code from Session");
            console.error(error);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: SiteCodeService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: SiteCodeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: SiteCodeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class UserRoleService {
    constructor(userService) {
        this.userService = userService;
    }
    hasRole(roleName) {
        const role = this.userService.getRoleByName(roleName);
        if (isNotNull(role)) {
            return true;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserRoleService, deps: [{ token: UserSessionService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserRoleService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: UserRoleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: UserSessionService }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * @updated Marilyn Mao marilyn.mao@gm.com
 * @since 11/10/2022
 */
const TOKEN_CONFIG = new InjectionToken('token.config');
class TokenService {
    /**
     * The constructor for this service.
     * @param cookieService
     * @param logoutService
     * @param httpClient
     * @param config
     */
    constructor(cookieService, logoutService, httpClient, config) {
        this.cookieService = cookieService;
        this.logoutService = logoutService;
        this.httpClient = httpClient;
        /**
         * How many minutes away for expiry should the token be refreshed.
         * @private
         */
        this.refreshTokenSecondsFromExpiry = REFRESH_TOKEN_SECONDS_FROM_EXPIRY;
        this.configure(config);
    }
    /**
     * Get client credentials
     * @param authenticationType
     */
    getClientCredentials(authenticationType) {
        const tokenConfig = this.tokenConfigurations.find(tokenConfig => tokenConfig.authenticationType === authenticationType);
        if (isNull(tokenConfig)) {
            console.error("No configuration found for auth type: " + authenticationType);
            return;
        }
        if (isNull(tokenConfig.clientId)) {
            console.error("No client Id found for auth type: '" + authenticationType);
            return;
        }
        if (isNull(tokenConfig.clientPassword)) {
            console.error("No client password found for auth type: '" + authenticationType);
            return;
        }
        return tokenConfig.clientId + ':' + tokenConfig.clientPassword;
    }
    /**
     * Processes the token response from the authz service.
     * @param response
     * @param authenticationType
     * @private
     */
    processTokenResponse(response, authenticationType) {
        if (isNull(response.access_token)) {
            console.error("No access token present in response.");
        }
        this.cookieService.put(this.getAccessTokenKeyForAuthenticationType(authenticationType), response.access_token, this.getCookieOptions());
        this.cookieService.put(this.getRefreshTokenKeyForAuthenticationType(authenticationType), response.refresh_token, this.getCookieOptions());
    }
    /**
     * Returns and validates the current access token.
     * @param authenticationType
     * @param shouldLogoutUser
     */
    returnAndValidateAccessToken(authenticationType, shouldLogoutUser) {
        const token = this.getAccessToken(authenticationType);
        if (this.isTokenValid(token, authenticationType)) {
            return token;
        }
        else {
            if (shouldLogoutUser) {
                this.logoutUser();
            }
            return token;
        }
    }
    /**
     * Determines if the user has a valid access token.
     */
    hasAValidToken(authenticationType) {
        const token = this.getAccessToken(authenticationType);
        if (isNull(token)) {
            return false;
        }
        return this.isTokenValid(token, authenticationType);
    }
    /**
     * Starts the user token refresh timer.
     */
    startTokenRefreshTimer(authenticationType) {
        this.startRefreshTimer(this.getRefreshToken(authenticationType), authenticationType);
    }
    /**
     * Executes a request for a new access token
     */
    refreshAccessToken(authenticationType) {
        this.stopRefreshTokenTimer();
        this.refreshToken(this.getRefreshToken(authenticationType), authenticationType).subscribe();
    }
    /**
     * Stops the refresh token timer.
     */
    stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }
    /**
     * Removes all access tokens generated by this app.
     */
    removeAccessTokens() {
        this.cookieService.remove(this.getAccessTokenKeyForAuthenticationType(USER_AUTHENTICATION_TYPE), this.getCookieOptions());
        this.cookieService.remove(this.getRefreshTokenKeyForAuthenticationType(USER_AUTHENTICATION_TYPE), this.getCookieOptions());
        this.stopRefreshTokenTimer();
    }
    /**
     * Configures this service.
     * @param configurations
     * @private
     */
    configure(configurations) {
        if (isEmptyArray(configurations)) {
            console.error("NO Configuration provided for token service!");
        }
        configurations.forEach((config) => {
            if (isNull(config.clientId)) {
                console.error("No clientId provided for token service!");
            }
            if (isNull(config.clientPassword)) {
                console.error("No clientPassword provided for token service!");
            }
            if (isNull(config.cookieDomain)) {
                if (isDevMode()) {
                    console.info('No domain passed in configuration. Using NAM as default.');
                }
                this.cookieDomain = DEFAULT_COOKIE_DOMAIN;
            }
            else {
                this.cookieDomain = config.cookieDomain;
            }
            let endpoint = config.apiGateway;
            if (isNotNull(config.apiPrefix)) {
                endpoint = endpoint + config.apiPrefix;
            }
            this.refreshTokenEndpoint = endpoint + REFRESH_TOKEN_PATH;
            if (isNotNull(config.refreshTokenSecondsFromExpiry)) {
                this.refreshTokenSecondsFromExpiry = config.refreshTokenSecondsFromExpiry;
            }
        });
        this.tokenConfigurations = configurations;
    }
    /**
     * Get client Id
     * @param authenticationType
     * @private
     */
    getClientId(authenticationType) {
        const tokenConfig = this.tokenConfigurations.find((config) => config.authenticationType === authenticationType);
        return tokenConfig.clientId;
    }
    /**
     * Returns the options for a cookie
     * @private
     */
    getCookieOptions() {
        const cookieOptions = {
            secure: true,
            domain: this.cookieDomain
        };
        if (isDevMode()) {
            cookieOptions.domain = null;
        }
        return cookieOptions;
    }
    /**
     * Validates the jwt token stored in the cookie.
     * Checks expiry time and issued client_id.
     * @param token
     * @param authenticationType
     */
    isTokenValid(token, authenticationType) {
        const decodedToken = this.decodeToken(token);
        if (isNull(decodedToken)) {
            return false;
        }
        if (this.isExpiredToken(token)) {
            return false;
        }
        //TODO: Maybe do domain check here too?
        // Or we can keep checking against client_id assuming client_id is different from site to site
        return decodedToken.client_id === this.getClientId(authenticationType);
    }
    /**
     * Decodes a JWT Token
     * @param token
     */
    decodeToken(token) {
        if (isNull(token)) {
            console.error("No token provided!");
            return;
        }
        let decodedToken;
        try {
            decodedToken = jwtDecode(token);
        }
        catch (e) {
            console.error(e);
            this.logoutUser();
        }
        return decodedToken;
    }
    /**
     * Checks to see if the token has expired.
     * @param token
     * @private
     */
    isExpiredToken(token) {
        const decodedToken = this.decodeToken(token);
        if (isNull(decodedToken)) {
            return true;
        }
        return Date.now() >= decodedToken.exp * 1000;
    }
    /**
     * Gets the access token key for 'user' or 'app' authentication type
     * @param authenticationType
     * @private
     */
    getAccessTokenKeyForAuthenticationType(authenticationType) {
        const tokenConfig = this.getTokenConfigForAuthenticationType(authenticationType);
        return this.getAccessKeyForAuthenticationType(tokenConfig, authenticationType) + tokenConfig.accessTokenKey;
    }
    /**
     * Gets the refresh token key for 'user' or 'app' authentication type
     * @param authenticationType
     * @private
     */
    getRefreshTokenKeyForAuthenticationType(authenticationType) {
        const tokenConfig = this.getTokenConfigForAuthenticationType(authenticationType);
        return this.getAccessKeyForAuthenticationType(tokenConfig, authenticationType) + tokenConfig.refreshTokenKey;
    }
    /**
     * Gets the token configuration for 'user' or 'app' authentication type
     * @param authenticationType
     * @private
     */
    getTokenConfigForAuthenticationType(authenticationType) {
        const tokenConfig = this.tokenConfigurations.find(tokenConfig => tokenConfig.authenticationType === authenticationType);
        if (isNull(tokenConfig)) {
            console.error("No configuration found for auth type: '" + authenticationType + "' in the configuration token!!");
            return;
        }
        return tokenConfig;
    }
    /**
     * Gets the access key for 'user', 'pin' or 'app' authentication type
     * @param tokenConfig
     * @param authenticationType
     * @private
     */
    getAccessKeyForAuthenticationType(tokenConfig, authenticationType) {
        let accessKey;
        if (authenticationType === USER_AUTHENTICATION_TYPE) {
            if (tokenConfig.loginMethod === PIN_LOGIN_METHOD) {
                accessKey = PIN_ACCESS_KEY;
            }
            if (tokenConfig.loginMethod === USER_CREDENTIALS_LOGIN_METHOD) {
                accessKey = USER_ACCESS_KEY;
            }
        }
        else {
            accessKey = APP_ACCESS_KEY;
        }
        return accessKey + '.' + this.getPlantCode(tokenConfig);
    }
    /**
     * Retrieves the user access token from the cookie.
     * @param authenticationType
     * @private
     */
    getAccessToken(authenticationType) {
        return this.cookieService.get(this.getAccessTokenKeyForAuthenticationType(authenticationType));
    }
    /**
     * Retrieves the refresh token from the cookie.
     * @param authenticationType
     * @private
     */
    getRefreshToken(authenticationType) {
        return this.cookieService.get(this.getRefreshTokenKeyForAuthenticationType(authenticationType));
    }
    /**
     * Gets the plant code
     * @param tokenConfig
     * @private
     */
    getPlantCode(tokenConfig) {
        if (isNull(tokenConfig.plantCode)) {
            return 'nam.';
        }
        else {
            return tokenConfig.plantCode;
        }
    }
    /**
     * Starts the token refresh timer.
     * @param refreshToken
     * @param authenticationType
     * @private
     */
    startRefreshTimer(refreshToken, authenticationType) {
        if (isNull(refreshToken)) {
            return this.logoutUser();
        }
        if (!this.isTokenValid(refreshToken, authenticationType)) {
            console.error("Invalid Token given!");
            return this.logoutUser();
        }
        const jwtTokenData = this.decodeToken(refreshToken);
        // Create a timeout to refresh the token x seconds before it expires
        const expires = new Date(jwtTokenData.exp * 1000);
        const timeout = expires.getTime() - Date.now() - (this.refreshTokenSecondsFromExpiry * 1000);
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken(refreshToken, authenticationType).subscribe(), timeout);
    }
    /**
     * Calls Authz service to refresh token
     * @param refreshToken
     * @param authenticationType
     * @private
     */
    refreshToken(refreshToken, authenticationType) {
        if (!this.hasActiveTokens(authenticationType)) {
            this.logoutUser();
        }
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Basic ' + window.btoa(this.getClientCredentials(authenticationType))
            })
        };
        const endpoint = this.refreshTokenEndpoint + refreshToken;
        return this.httpClient.post(endpoint, {}, httpOptions)
            .pipe(map((response) => {
            if (isNotNull(response)) {
                this.processTokenResponse(response, authenticationType);
                this.startRefreshTimer(response.refresh_token, authenticationType);
            }
        })).pipe(catchError((err, caught) => {
            console.log("Error refreshing Token", err);
            if (err instanceof HttpErrorResponse) {
                if (err.status === 401) {
                    this.logoutUser();
                }
            }
            return throwError(err);
        }));
    }
    /**
     * Determines if the tokens are active and valid
     * @private
     */
    hasActiveTokens(authenticationType) {
        return this.isTokenValid(this.getAccessToken(authenticationType), authenticationType) && this.isTokenValid(this.getRefreshToken(authenticationType), authenticationType);
    }
    /**
     * Clears access tokens and logs the user out.
     * @private
     */
    logoutUser() {
        this.removeAccessTokens();
        this.logoutService.sendUserToLogout();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: TokenService, deps: [{ token: i1$2.CookieService }, { token: LogoutService }, { token: i1$1.HttpClient }, { token: TOKEN_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: TokenService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: TokenService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.CookieService }, { type: LogoutService }, { type: i1$1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TOKEN_CONFIG]
                }] }]; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const AUTH_CONFIG = new InjectionToken('auth.config');
const UI_LOGIN_PATH = new InjectionToken('uiLogin.path');
/**
 *
 */
class AuthenticationService {
    /**
     * Constructor for this service.
     * @param globalData
     * @param router
     * @param activatedRoute
     * @param httpClient
     * @param loadingService
     * @param userService
     * @param dialog
     * @param tokenService
     * @param logoutService
     * @param configurationInjector
     * @param config
     */
    constructor(globalData, router, activatedRoute, httpClient, loadingService, userService, dialog, tokenService, logoutService, configurationInjector, uiLoginPath, config) {
        this.globalData = globalData;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.httpClient = httpClient;
        this.loadingService = loadingService;
        this.userService = userService;
        this.dialog = dialog;
        this.tokenService = tokenService;
        this.logoutService = logoutService;
        this.configurationInjector = configurationInjector;
        /**
         * Subject for to see if user is logged in.
         */
        this.isUserLoggedIn = new BehaviorSubject(false);
        /**
         * The ui login path
         */
        this.uiLoginPath = '/login';
        this.configure(config, uiLoginPath);
    }
    /**
     * Gets client credentials for auth type
     * @param authenticationType
     */
    getClientCredentials(authenticationType) {
        const authConfig = this.getAuthConfigForAuthenticationType(authenticationType);
        if (isNull(authConfig.clientId)) {
            console.error("No client Id found for auth type: '" + authenticationType + "' in the configuration token!!");
            return;
        }
        if (isNull(authConfig.clientPassword)) {
            console.error("No client id found for auth type: '" + authenticationType + "' in the configuration token!!");
            return;
        }
        return authConfig.clientId + ':' + authConfig.clientPassword;
    }
    /**
     * Logs the user in with full auth
     * @param userId
     * @param password
     */
    loginWithIdAndPassword(userId, password) {
        const endpoint = this.getLoginEndpoint(USER_AUTHENTICATION_TYPE).replace(USER_LOGIN_USER_KEY, userId) + password;
        this.login(endpoint, USER_AUTHENTICATION_TYPE);
    }
    /**
     * Logs the user in with a pin
     * @param userPin
     */
    loginWithPin(userPin) {
        const endpoint = this.getLoginEndpoint(USER_AUTHENTICATION_TYPE).replace(PIN_LOGIN_PIN_KEY, userPin.toLocaleString());
        this.login(endpoint, USER_AUTHENTICATION_TYPE);
    }
    /**
     * Logs the application in with App client credentials.
     */
    loginWithAppId() {
        const endpoint = this.getLoginEndpoint(APP_AUTHENTICATION_TYPE);
        this.login(endpoint, APP_AUTHENTICATION_TYPE);
    }
    /**
     * Logs the user out and removes all access tokens.
     */
    logout() {
        this.isUserLoggedIn.next(false);
        this.tokenService.removeAccessTokens();
        this.logoutService.sendUserToLogout();
    }
    /**
     * Opens the warning dialog login fails.
     * @param error
     */
    openWarningDialog(error) {
        const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
            width: '300px',
            data: {
                title: 'Login Failed!',
                message: this.getErrorMessageToDisplay(error),
                titleIcon: 'report',
                yesLabel: 'Ok'
            },
            disableClose: false
        });
    }
    /**
     * Configures this service.
     * @private
     */
    configure(configurations, uiLoginPath) {
        if (isNull(uiLoginPath)) {
            if (isDevMode()) {
                console.info('Using ' + this.uiLoginPath + ' as the default login path.');
            }
        }
        else {
            this.uiLoginPath = uiLoginPath;
        }
        if (isEmptyArray(configurations)) {
            console.error("NO Configuration provided for auth service!");
        }
        configurations.forEach((config) => {
            if (isNull(config.clientId)) {
                console.error("No clientId provided for auth service!");
            }
            if (isNull(config.clientPassword)) {
                console.error("No clientPassword provided for auth service!");
            }
            if (isNull(config.authenticationType)) {
                console.error('No auth level provided in configuration!!');
            }
            if (config.authenticationType === USER_AUTHENTICATION_TYPE) {
                if (isNull(config.loginMethod)) {
                    console.error('No login method provided in configuration!!');
                }
            }
        });
        this.authenticationConfigurations = configurations;
    }
    /**
     * Gets the auth config
     * @param authenticationType
     * @private
     */
    getAuthConfigForAuthenticationType(authenticationType) {
        const authConfig = this.authenticationConfigurations.find(config => config.authenticationType === authenticationType);
        if (isNull(authConfig)) {
            console.error("No configuration found for auth type: '" + authenticationType + "' in the configuration token!!");
            return;
        }
        return authConfig;
    }
    /**
     * Get the login endpoint
     * @param authenticationType
     * @private
     */
    getLoginEndpoint(authenticationType) {
        const authConfig = this.getAuthConfigForAuthenticationType(authenticationType);
        if (isNull(authConfig.apiGateway)) {
            console.error("No API gateway found for auth type: '" + authenticationType + "' in the configuration token!!");
        }
        let endpoint = authConfig.apiGateway;
        if (isNotNull(authConfig.apiPrefix)) {
            endpoint = endpoint + authConfig.apiPrefix;
        }
        if (authenticationType === USER_AUTHENTICATION_TYPE) {
            if (authConfig.loginMethod === PIN_LOGIN_METHOD) {
                endpoint = endpoint + PIN_LOGIN_PATH;
            }
            if (authConfig.loginMethod === USER_CREDENTIALS_LOGIN_METHOD) {
                endpoint = endpoint + USER_LOGIN_PATH;
            }
        }
        else {
            endpoint = endpoint + OATH_CLIENT_CREDENTIALS_PATH;
        }
        return endpoint;
    }
    /**
     * Executes the user login
     * @param endpoint
     * @param authenticationType
     * @private
     */
    login(endpoint, authenticationType) {
        this.loadingService.startLoading();
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Basic ' + window.btoa(this.getClientCredentials(authenticationType))
            })
        };
        this.httpClient.post(endpoint, {}, httpOptions)
            .pipe(finalize(() => {
            this.loadingService.finishLoading();
        }))
            .subscribe((tokenResponse) => {
            if (isNotNull(tokenResponse)) {
                this.tokenService.processTokenResponse(tokenResponse, authenticationType);
                this.userService.getUserSession(authenticationType, true).then((sessionResponse) => {
                    this.userService.processUserSessionResponse(sessionResponse);
                    if (authenticationType === USER_AUTHENTICATION_TYPE) {
                        this.isUserLoggedIn.next(true);
                    }
                    this.tokenService.startTokenRefreshTimer(authenticationType);
                });
                this.redirectUser();
            }
        }, (error) => {
            this.openWarningDialog(error);
        });
    }
    /**
     * Navigates the user after authentication.
     * @private
     */
    redirectUser() {
        const redirectPath = this.activatedRoute.snapshot.queryParamMap.get(REDIRECT_QUERY_PARAM);
        if (isNotNull(redirectPath)) {
            this.navigateUser(redirectPath);
        }
        else {
            this.navigateUser('/');
        }
    }
    /**
     * Navigates the user.
     * @param path
     * @private
     */
    navigateUser(path) {
        this.router.navigate([path]);
    }
    /**
     * Returns the error message for the login error.
     * TODO: Update after updating authz service to return more meaniful error messages.
     * @param error
     * @private
     */
    getErrorMessageToDisplay(error) {
        let errorMessage = '';
        console.error(error);
        if (isNull(error)
            || isNull(error.status)
            || error.status === 0
            || error.status === 404
            || error.status === 503
            || error.status === 504) {
            errorMessage = 'Could not reach server, please re-login in a few minutes. If this issue persists, please contact a system administrator.';
        }
        else if (error.status === 401 || error.status === 403) {
            const errorFromAuth = error.error['error_description'];
            if (errorFromAuth.includes('TonicClient#login(TonicModel)')) {
                errorMessage = 'Please verify your login credentials.';
            }
            if (errorFromAuth.includes('Security')) {
                errorMessage = 'Your user or pin has not been configured for this application.';
            }
        }
        else if (error.status === 500) {
            errorMessage = 'Log in failed. Please contact a system administrator.';
        }
        else {
            const errorBody = error.error;
            if (Array.isArray(errorBody.errorList) && errorBody.errorList.length > 0) {
                errorMessage = errorBody.errorList.map(e => e.message).join(', ');
            }
            else if (isNotNull(errorBody.globalErrors) && errorBody.globalErrors.length > 0) {
                errorMessage = errorBody.globalErrors[0].code;
            }
            else {
                errorMessage = error.statusText;
            }
        }
        return errorMessage;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AuthenticationService, deps: [{ token: i1.GlobalData }, { token: i2.Router }, { token: i2.ActivatedRoute }, { token: i1$1.HttpClient }, { token: i4$1.LoadingService }, { token: UserSessionService }, { token: i6.MatDialog }, { token: TokenService }, { token: LogoutService }, { token: i4.ConfigurationInjector }, { token: UI_LOGIN_PATH }, { token: AUTH_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AuthenticationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AuthenticationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.GlobalData }, { type: i2.Router }, { type: i2.ActivatedRoute }, { type: i1$1.HttpClient }, { type: i4$1.LoadingService }, { type: UserSessionService }, { type: i6.MatDialog }, { type: TokenService }, { type: LogoutService }, { type: i4.ConfigurationInjector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [UI_LOGIN_PATH]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }]; } });

class CommonReactiveService {
    /**
     *
     */
    constructor() {
        /**
         * Injecting RSocketService
         */
        this.rSocketService = inject(RSocketService);
    }
    /**
     * RSocketRxjs does not support injecting mutiple URL(Create Singleton instance)
     *  via module. Hence adding this method to override the config
     * values
     *
     * @param config RSocketRxjsModuleConfig
     */
    overrideRootSettings(config) {
        this.rSocketService.connect(config);
    }
    /**
      * Assigns the endpoint the service will be hitting
      *
      * @param apiEndpoint This is the endpoint this service will be hitting
      */
    setApiEndpoint(apiEndpoint) {
        this.reactiveApiEndpoint = apiEndpoint;
    }
    /**
     * This method provides the request stream for search API
     *
     * @param filterPredicate reactiveFilterDataList
     * @returns Observable<ResponseModel<T>>
     */
    getSearchRequestStream({ filterPredicate, endpointOverride = '', }) {
        const reactiveApiEndpoint = this.getEndpoint(endpointOverride);
        return this.rSocketService.route(reactiveApiEndpoint)
            .data(filterPredicate)
            .requestStream().pipe(repeat());
    }
    /**
     * This method provides Rsocket state
     */
    getRsocketState() {
        return this.rSocketService.state();
    }
    /**
   * Common Code for the Endpoint fetches
   * @param endpointOverride: OPTIONAL: override endpoint passed in
   *
   * @returns string: reactive stream path
   */
    getEndpoint(endpointOverride) {
        if (isNotNull(endpointOverride)) {
            return endpointOverride;
        }
        else {
            return this.reactiveApiEndpoint;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonReactiveService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonReactiveService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: CommonReactiveService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 *
 */
/**
 * @author Nilesh Joshi nilesh.joshi@gm.com
 * @since 5/17/2023
 */
class OidcAuthModule {
    static forRoot(userManagerSettings) {
        return {
            ngModule: OidcAuthModule,
            providers: [
                OidcAuthService,
                {
                    provide: OIDC_CONFIG_SETTING,
                    useValue: userManagerSettings
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: OidcAuthModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AppLoadService, AuthenticationService, CommonFormService, CommonReactiveService, CommonService, GuidService, LogoutService, OIDC_CONFIG_SETTING, OidcAuthModule, OidcAuthService, PermissionService, SiteCodeService, TOKEN_CONFIG, TokenService, UI_LOGIN_PATH, UserRoleService, UserSessionService, UserTypeService };
